// CRA bakes REACT_APP_* values into the bundle, so a build made with the
// production hosts can never be safely reused on admin.dev. Resolve the
// development hosts from the runtime hostname first; keep the build-time
// values as the production/override path.
const isDevAdminHost = window.location.hostname.endsWith(".dev.smartsway.com");

/** sal-core — auth, users, subscriptions (admin). */
const SAL_CORE_ORIGIN = isDevAdminHost
  ? "https://auth.dev.smartsway.com"
  : process.env.REACT_APP_SAL_CORE_URL || "https://auth-subs.salback.shop";

/** sal-api — CMS, feedback, healing (legacy admin routes). */
const SAL_API_ORIGIN = isDevAdminHost
  ? "https://api.dev.smartsway.com"
  : process.env.REACT_APP_SAL_API_URL || "https://salapi.smartsway.com";

const apiEnv: Record<
  string,
  {
    baseUrl: string;
    endPoint: string;
    legacyBaseUrl: string;
    legacyEndPoint: string;
    mediaUrl: string;
    mediaEndPoint: string;
  }
> = {
  development: {
    baseUrl: `${SAL_CORE_ORIGIN}/v1/admin/`,
    endPoint: `${SAL_CORE_ORIGIN}/v1/`,
    legacyBaseUrl: `${SAL_API_ORIGIN}/admin/`,
    legacyEndPoint: `${SAL_API_ORIGIN}/`,
    mediaUrl: "https://saladmin.smartsway.com:3000",
    mediaEndPoint: "https://saladmin.smartsway.com:3000/media",
  },
  production: {
    baseUrl: `${SAL_CORE_ORIGIN}/v1/admin/`,
    endPoint: `${SAL_CORE_ORIGIN}/v1/`,
    legacyBaseUrl: `${SAL_API_ORIGIN}/admin/`,
    legacyEndPoint: `${SAL_API_ORIGIN}/`,
    mediaUrl: "https://saladmin.smartsway.com:3000",
    mediaEndPoint: "https://saladmin.smartsway.com:3000/media",
  },
};

// -----------------------------------------------------------------------------

const appEnv = process.env.REACT_APP_ENV || "development";
export let BASE_URL: string = apiEnv[appEnv].baseUrl;
/** sal-core v1 root — e.g. `GET /v1/user/me` for session validation. */
export let APIEndpoint: string = apiEnv[appEnv].endPoint;
/** sal-api admin prefix — CMS, feedback, etc. */
export let LEGACY_BASE_URL: string = apiEnv[appEnv].legacyBaseUrl;
/** sal-api root — e.g. `GET /healing`. */
export let LEGACY_API_ENDPOINT: string = apiEnv[appEnv].legacyEndPoint;
export let MEDIA_URL: string = apiEnv[appEnv].mediaUrl;
let MediaAPIEndpoint: string = apiEnv[appEnv].mediaEndPoint;

// -----------------------------------------------------------------------------

const FETCH_TIMEOUT_MS = 30_000;

const fetchWithTimeout = (
  input: RequestInfo,
  init?: RequestInit,
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    FETCH_TIMEOUT_MS,
  );

  return fetch(input, { ...init, signal: controller.signal }).finally(() => {
    window.clearTimeout(timeoutId);
  });
};

const currentOrigin = window.location.origin;

const authHeaders = (
  auth_token: string,
  locale?: string,
): Record<string, string> => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${auth_token}`,
  "x-localization": locale || "ar",
  origin: currentOrigin,
});

export const get = <T>(
  endpoint: string,
  locale?: string,
  auth_token: string = "invalid",
): Promise<T> =>
  fetchWithTimeout(BASE_URL + endpoint, { headers: authHeaders(auth_token, locale) })
    .then(handleRes)
    .catch(() => null);

/** sal-api admin routes (CMS, feedback, …). */
export const legacyGet = <T>(
  endpoint: string,
  locale?: string,
  auth_token: string = "invalid",
): Promise<T> =>
  fetchWithTimeout(LEGACY_BASE_URL + endpoint, {
    headers: authHeaders(auth_token, locale),
  })
    .then(handleRes)
    .catch(() => null);

// -----------------------------------------------------------------------------

export const getHealing = <T>(
  locale?: string,
  auth_token: string = "invalid",
): Promise<T> =>
  fetch(`${LEGACY_API_ENDPOINT}healing`, {
    headers: authHeaders(auth_token, locale),
  })
    .then(handleRes)
    .catch(() => null);

// -----------------------------------------------------------------------------

export const post = <T>(
  endpoint: string,
  body: T,
  locale?: string,
  method: string = "POST",
  auth_token: string = "invalid",
): Promise<T> =>
  fetch(BASE_URL + endpoint, {
    method: method || "POST",
    headers: authHeaders(auth_token, locale),
    body: JSON.stringify(body),
  })
    .then(handleRes)
    .catch(() => null);

export const legacyPost = <T>(
  endpoint: string,
  body: T,
  locale?: string,
  method: string = "POST",
  auth_token: string = "invalid",
): Promise<T> =>
  fetch(LEGACY_BASE_URL + endpoint, {
    method: method || "POST",
    headers: authHeaders(auth_token, locale),
    body: JSON.stringify(body),
  })
    .then(handleRes)
    .catch(() => null);

// -----------------------------------------------------------------------------

export const put = <T>(
  endpoint: string,
  body: T,
  locale?: string,
  auth_token: string = "invalid",
): Promise<T> =>
  fetch(BASE_URL + endpoint, {
    method: "PUT",
    headers: authHeaders(auth_token, locale),
    body: JSON.stringify(body),
  })
    .then(handleRes)
    .catch(() => null);

export const legacyPut = <T>(
  endpoint: string,
  body: T,
  locale?: string,
  auth_token: string = "invalid",
): Promise<T> =>
  fetch(LEGACY_BASE_URL + endpoint, {
    method: "PUT",
    headers: authHeaders(auth_token, locale),
    body: JSON.stringify(body),
  })
    .then(handleRes)
    .catch(() => null);

// -----------------------------------------------------------------------------

export const editPrice = ({
  id,
  price,
  locale,
  auth_token = localStorage.getItem("__auth") || "invalid",
}: {
  id: string;
  price: number;
  locale?: string;
  auth_token?: string;
}) =>
  fetch(BASE_URL + `subscription/order/price/${id}`, {
    method: "PUT",
    headers: authHeaders(auth_token, locale),
    body: JSON.stringify({ price }),
  });

// -----------------------------------------------------------------------------

export const deleteMedia = (
  path: string,
  auth_token: string = "invalid",
): Promise<void> =>
  fetch(MediaAPIEndpoint + "/" + path, {
    method: "delete",
    headers: authHeaders(auth_token),
  })
    .then(handleRes)
    .catch(() => null);

// -----------------------------------------------------------------------------

export const deleteMethod = (
  endpoint: string,
  auth_token: string = "invalid",
): Promise<void> =>
  fetch(BASE_URL + endpoint, {
    method: "delete",
    headers: authHeaders(auth_token),
  })
    .then(handleRes)
    .catch(() => null);

export const legacyDeleteMethod = (
  endpoint: string,
  auth_token: string = "invalid",
): Promise<void> =>
  fetch(LEGACY_BASE_URL + endpoint, {
    method: "delete",
    headers: authHeaders(auth_token),
  })
    .then(handleRes)
    .catch(() => null);

// -----------------------------------------------------------------------------

const handleRes = async (res: Response) => {
  if (res.status === 401 && window.location.pathname !== "/login") {
    window.localStorage.clear();
    window.location.reload();
  }
  try {
    const jsonRes = await res.json();
    if (!res.ok) throw jsonRes;
    return jsonRes;
  } catch (e) {
    return new Promise((resolve) => resolve(res.ok));
  }
};

// -----------------------------------------------------------------------------

export const getAmazonConfig = () => {
  return {
    bucketName: "BUCKET-NAME",
    dirName: "DIR-NAME",
    region: "REGION",
    accessKeyId: "ACCESS-KEY",
    secretAccessKey: "SECRET-KEY",
    s3Url: "S3-BUCKET-URL",
  };
};

// -----------------------------------------------------------------------------

export const uploadFile = async (
  file: File,
  auth_token: string = "invalid",
) => {
  const data = new FormData();
  data.append("file", file);

  const response = await fetch(MediaAPIEndpoint, {
    headers: {
      Authorization: `Bearer ${auth_token}`,
    },
    method: "POST",
    body: data,
  });

  const json = await response.json();
  if (json.url) return `${MEDIA_URL}/${json.url}`;
  alert("Failed to upload file!");
};
